import { store } from '@/store';
import type {
    DocumentCategory,
    DocumentType,
    DurableJobExpandOpts,
    ICreateGptParseRequestParams,
    IDurableJob,
    IFetchDurableJobsParams,
    IUserConfirmation,
} from '@condo/domain';
import type { AxiosInstance } from 'axios';
import { omit } from 'lodash-es';
import { type IApiClients, getClient } from './api-client';

export const fetchDurableJobs = async (params?: IFetchDurableJobsParams): Promise<{ durableJobs: IDurableJob[]; total: number }> =>
    getClient('basecamp')
        .get('/durable-jobs', { params })
        .then(response => response.data);

export const fetchDurableJob = async (id: string): Promise<IDurableJob> =>
    getClient('basecamp')
        .get(`/durable-jobs/${id}`)
        .then(response => response.data.durableJob);

export const fetchUnseenDurableJobs = async (expand?: DurableJobExpandOpts): Promise<{ durableJobs: IDurableJob[]; total: number }> =>
    getClient('basecamp')
        .get('/durable-jobs/unseen', { params: { expand } })
        .then(response => response.data);

export const fetchDurableJobsList = async (): Promise<
    { name: string; cron: string | null; lastSuccessfulRun: Partial<IDurableJob> | null; source: keyof IApiClients }[]
> => {
    const environment = store.getters['ui/environment'];

    const promises = [
        getClient('basecamp')
            .get('/durable-jobs/list')
            .then(response =>
                response.data.durableJobs.map((job: { name: string; cron: string | null; lastSuccessfulRun: Partial<IDurableJob> | null }) => ({
                    ...job,
                    source: 'basecamp',
                })),
            ),
    ];

    if (environment !== 'local') {
        promises.push(
            getClient('basecampMaintenance')
                .get('/durable-jobs/list')
                .then(response =>
                    response.data.durableJobs.map((job: { name: string; cron: string | null; lastSuccessfulRun: Partial<IDurableJob> | null }) => ({
                        ...job,
                        source: 'basecampMaintenance',
                    })),
                ),
            getClient('basecampPersister')
                .get('/durable-jobs/list')
                .then(response =>
                    response.data.durableJobs.map((job: { name: string; cron: string | null; lastSuccessfulRun: Partial<IDurableJob> | null }) => ({
                        ...job,
                        source: 'basecampPersister',
                    })),
                ),
        );
    }

    return (await Promise.all(promises)).flat();
};

export const markDurableJobsSeen = async (ids: string[]): Promise<IDurableJob[]> =>
    getClient('basecamp')
        .put('/durable-jobs/seen', { ids })
        .then(response => response.data.durableJobs);

export const cancelDurableJob = async (jobUuid: string, client: keyof IApiClients): Promise<void> =>
    (getClient(client) as AxiosInstance).delete(`/durable-jobs/${jobUuid}`);
export const rerunDurableJob = async (jobUuid: string, client: keyof IApiClients): Promise<void> =>
    (getClient(client) as AxiosInstance).post(`/durable-jobs/${jobUuid}/rerun`);

export const createGptParseRequests = async (data: ICreateGptParseRequestParams): Promise<void> =>
    getClient('basecamp').post('/documents/gpt-parse-request', data);

export const createMfhAttributeGptParseRequest = async (mfhId: number): Promise<void> =>
    getClient('basecamp').post(`/mfh/${mfhId}/attribute-gpt-parse-request`);

export const scheduleDurableJob = async (
    params: {
        name: string;
        params?: Record<string, any>;
        meta?: Record<string, any>;
        opts?: { runAfter?: number; priority?: number };
    },
    client: keyof IApiClients,
): Promise<IDurableJob> => (getClient(client) as AxiosInstance).post('/durable-jobs', params).then(response => response.data.durableJob);

export const scheduleDocumentDurableJob = async (params: {
    documentCategory?: DocumentCategory;
    template: DocumentType;
    output?: {
        type?: string;
        targetFolderId?: string;
        targetFolderName?: string;
        convertToPdf?: boolean;
        exportXlsToBucket?: boolean;
        targetFolderIsPersonalDrive?: boolean;
        removeOriginalOutput?: boolean;
    };
    params?: Record<string, any>;
}): Promise<IDurableJob> =>
    getClient('basecamp')
        .post('/documents/durable-jobs', params)
        .then(response => response.data.durableJob);

// ======================= USER CONFIRMATIONS =======================

// @todo add origin somehow
export const fetchUserConfirmations = async (): Promise<IUserConfirmation[]> =>
    getClient('basecamp')
        .get('/user-confirmations')
        .then(response => response.data.userConfirmations);

export const settleUserConfirmation = async (query: Partial<IUserConfirmation>): Promise<void> => {
    if (!query.origin) {
        throw new Error(`Need to have an origin for settling a user-confirmation`);
    }
    if (query.origin.includes('basecamp')) {
        return getClient('basecamp').post(`/user-confirmations/${query.userConfirmationId}`, omit(query, 'origin'));
    }
    throw new Error(`Unsupported confirmation-origin: ${query.origin}`);
};
